import React from "react";
import './VisitCard.css'
import PropTypes from "prop-types";

export default function VisitCard({
  statusColor,
  icon,
  title,
  tags,
  expanded,
  toggle,
  children,
  visitDate,
  additionalVisitDate
}) {
  const formatDate = (dateStr) => {
    if (!dateStr) return ""; // or return "N/A", depending on what you want

    const date = new Date(dateStr);
    if (isNaN(date)) return ""; // handle invalid date strings

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  const getIconColor = (statusColor) => {
    switch (statusColor) {
      case "warning":
        return "#A55900"; // amber-like
      case "info":
        return "#3345DF"; // blue
      case "success":
        return "#2B8000"; // green
      case "cancel":
        return "#DD1F36"; //red
      default:
        return "#767676"; // fallback grey
    }
  };

  return (
    <div
      className={`card mb-4 has-border-${statusColor} ${expanded ? "expanded" : ""
        }`}
    >
      <header
        className={`card-header is-clickable visitcard-header`}
        onClick={toggle}
      >
        {/* Main flex row for left icon+title/tags and right toggle */}
        <div className="main-flex">
          {/* Icon and title/tags vertical stack */}
          <div className="icon-title-stack">
            <span className="icon icon-margin" >
              <svg
                data-testid="main-icon"
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 -960 960 960"
                width="24"
                fill={getIconColor(statusColor)}
                style={{ color: getIconColor(statusColor) }}
              >
                <path d={icon} />
              </svg>
            </span>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <strong
                style={{
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#363636",
                }}
              >
                {title}
                {['Visit confirmed', 'Visit cancelled', 'Visit completed'].includes(title) && (
                  formatDate(visitDate) ? ` - ${formatDate(visitDate)}` : ''
                )}
              </strong>
              <>
               {additionalVisitDate && (
                <div className="is-flex is-align-items-center">
                  <span className="icon mr-1">
                    <svg
                      style={{ fill: "#a55900" }}
                      xmlns="http://www.w3.org/2000/svg"
                      height="24px"
                      viewBox="0 -960 960 960"
                      width="24px"
                      fill="212121"
                    >
                      <path d="m560-120-57-57 144-143H200v-480h80v400h367L503-544l56-57 241 241-240 240Z" />
                    </svg>
                  </span>
                  <span className="pt-1">
                    <strong style={{ color: "#a55900" }}>
                      {`Additional visit: ${formatDate(additionalVisitDate)}`}
                    </strong>
                  </span>
                </div>
              )}
              </>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  marginTop: "0.25em",
                }}
              >
                {tags.map((tag) => (
                  <span
                    key={tag.label}
                    className={`tag ${tag.className} mr-2`}
                    style={{
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                      backgroundColor: "#FAFAFA",
                      color: "#000000",
                      height: "1.5em",
                      padding: ".25em .5em",
                      fontSize: "13px",
                      marginRight: "4px",
                      marginTop: "5px",
                      display: "inline-flex",
                      alignItems: "center",
                    }}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Toggle icon */}
          <button
            className="button is-icon visitcard-toggle-button"
            title="Open / Close"
            aria-label="Open / Close"
            type="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              if (toggle) toggle();
            }}
          >
            <span className="icon">
              <svg
                data-testid="toggle-icon"
                style={{
                  transform: expanded ? "rotate(270deg)" : "rotate(90deg)",
                  fill: getIconColor(statusColor),
                  width: "24px",
                  height: "24px",
                  transition: "transform 0.2s",
                }}
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 -960 960 960"
                width="24"
                aria-hidden="true"
                focusable="false"
              >
                <path d="m517.847-480-184-184L376-706.153 602.153-480 376-253.847 333.847-296l184-184Z" />
              </svg>
            </span>
          </button>
        </div>
      </header>
      {expanded && (
        <>
          <hr className="m-0" />
          <div className="card-content" data-testid="card-content"  style={
    ['Visit confirmed', 'Visit cancelled', 'Visit completed'].includes(title)
      ? { padding: '0.5rem 1rem' }
      : {}
  }>
            <div className="content">{children}</div>
          </div>
        </>
      )}
    </div>
  );
}


VisitCard.propTypes = {
  statusColor: PropTypes.string,                 // e.g., "warning", "info", etc.
  icon: PropTypes.node,                          // React element or SVG
  title: PropTypes.string.isRequired,            // likely required
  tags: PropTypes.arrayOf(PropTypes.string),     // adjust if tags are objects
  expanded: PropTypes.bool,
  toggle: PropTypes.func.isRequired,             // click handler
  children: PropTypes.node,                      // nested JSX
  visitDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
};